<app-menu-bar></app-menu-bar>

<div class="gateways-page">
    <div class="gateways-container">
        <!-- The nav branches by role itself (admin tabs / Files / Gateways),
             so every portal page renders it — same as /account and /my-files. -->
        <app-admin-nav></app-admin-nav>

        @if (!isAdmin) {
            <h1 class="title">My Gateways</h1>
        }

        @if (loading) {
            <div class="loading-state">
                <span class="spinner" role="status" aria-label="Loading gateways"></span>
            </div>
        } @else if (isAdmin) {
            <!-- ============ ADMIN TABLE VIEW ============ -->
            <div class="admin-toolbar">
                <input class="search" type="text" autocomplete="off" [(ngModel)]="search"
                    placeholder="Search by customer, UID, activation code or notes" />
                <button class="codes-btn" type="button" (click)="downloadActivationCodes()"
                    [disabled]="downloadingCodes"
                    title="Download every activation code still in the pool, oldest first">
                    {{ downloadingCodes ? 'Preparing…' : 'Download activation codes' }}
                </button>
                <button class="add-btn" type="button" (click)="openProvisionModal()">Add new gateway</button>
            </div>

            @if (filteredGateways.length === 0) {
                <p class="state-text">No gateways found.</p>
            } @else {
                <div class="table-wrap">
                    <table class="gw-table">
                        <!-- Fixed layout with explicit widths. Without it the table sizes to
                             max-content: one long customer name or note pushed the whole table
                             past the container and forced a horizontal scrollbar even on a wide
                             screen. Customer takes the remaining slack, so it gets the most room. -->
                        <colgroup>
                            <col class="col-customer" />
                            <col class="col-uid" />
                            <col class="col-code" />
                            <col class="col-date" />
                            <col class="col-notes" />
                            <col class="col-conn" />
                            <col class="col-active" />
                            <col class="col-tools" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Customer</th>
                                <th>UID</th>
                                <th>Activate key</th>
                                <th>Date activated</th>
                                <th>Notes</th>
                                <th class="center">Connected</th>
                                <th class="center">Active</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- track by index: the cloud id is null for gateways the cloud
                                 does not return, and three uids are shared by two rows each. -->
                            @for (gw of filteredGateways; track $index) {
                                <tr [class.no-local]="gw.hasLocalRecord === false">
                                    <td>
                                        <input class="meta-input" type="text" [ngModel]="gw.customerName"
                                            (ngModelChange)="gw.customerName = $event" (blur)="saveMeta(gw, 'customerName')"
                                            [title]="gw.customerName || ''"
                                            placeholder="—" [disabled]="savingUid === gw.uid" />
                                    </td>
                                    <td class="uid">{{ formatUid(gw.uid) }}</td>
                                    <td>{{ gw.code || '—' }}</td>
                                    <td>
                                        <!-- Ours, not the cloud's. The backend refuses to clear it,
                                             because it is also the already-claimed guard. -->
                                        <input class="meta-input date" type="date"
                                            [ngModel]="dateInputValue(gw)"
                                            (ngModelChange)="gw.activationDate = $event || null"
                                            (change)="saveMeta(gw, 'activationDate')"
                                            [disabled]="savingUid === gw.uid" />
                                    </td>
                                    <td>
                                        <input class="meta-input notes" type="text" [ngModel]="gw.notes"
                                            (ngModelChange)="gw.notes = $event" (blur)="saveMeta(gw, 'notes')"
                                            [title]="gw.notes || ''"
                                            placeholder="—" [disabled]="savingUid === gw.uid" />
                                    </td>
                                    <!-- Connected and Active are the cloud's, and the only reason
                                         the cloud is called. A gateway the cloud does not return has
                                         no state to show, so it gets a dash and no checkbox — what
                                         the old site did. -->
                                    <td class="center">
                                        @if (!gw.inCloud) {
                                            <span class="not-in-cloud" title="The cloud does not return this gateway">—</span>
                                        } @else if (gw.isConnected === 1) {
                                            <fa-icon [icon]="faCircleCheck" class="conn connected" title="Connected"></fa-icon>
                                        } @else {
                                            <fa-icon [icon]="faCircleExclamation" class="conn disconnected" title="Not connected"></fa-icon>
                                        }
                                    </td>
                                    <td class="center">
                                        @if (gw.inCloud) {
                                            <input type="checkbox" [checked]="gw.active === 1" (change)="toggleActive(gw, $event)" />
                                        }
                                    </td>
                                    <td class="center">
                                        <div ngbDropdown container="body" class="tools-dropdown">
                                            <button class="tools-btn" type="button" ngbDropdownToggle>Tools</button>
                                            <div ngbDropdownMenu class="gw-tools-menu">
                                                <button ngbDropdownItem (click)="deletePermanent(gw)">Delete gateway</button>
                                                <button ngbDropdownItem (click)="empty(gw)">Remove customer</button>
                                                <button ngbDropdownItem (click)="openTool('replace', gw)">Replace gateway UID</button>
                                                <!-- Both act on the cloud ACCOUNT via its cloudUserId, so
                                                     they are meaningless on a gateway nobody has claimed —
                                                     the request would carry a null id and fail validation.
                                                     The old site offered them on every row regardless. -->
                                                @if (gw.cloudUserId) {
                                                    <button ngbDropdownItem (click)="openTool('username', gw)">Change username</button>
                                                    <button ngbDropdownItem (click)="openTool('password', gw)">Change password</button>
                                                }
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            }
        } @else {
            <!-- ============ CUSTOMER CARD VIEW ============ -->
            @if (gateways.length === 0) {
                <p class="state-text">You have no gateways yet.</p>
            } @else {
                <div class="gateways-grid">
                    @for (gw of gateways; track gw.id) {
                        <div class="gateway-card">
                            <!-- Description then Activation code, as the old site's card had it.
                                 No UID: it was never shown to customers. Description is ours
                                 (the nickname), falling back to the cloud's own descr. -->
                            <!-- The field is read-only until the pencil beside the label is
                                 pressed, so the card reads as text and only becomes a form
                                 when the customer asks for it. Enter commits, Escape reverts,
                                 and blur commits (the same as the admin table). -->
                            <div class="card-row card-row--stacked">
                                <span class="label">
                                    Description
                                    <button type="button" class="desc-pencil-btn"
                                        (click)="beginEditDescription(gw)"
                                        [disabled]="savingUid === gw.uid"
                                        title="Edit description" aria-label="Edit description">
                                        <fa-icon [icon]="faPencil"></fa-icon>
                                    </button>
                                </span>
                                @if (editingUid === gw.uid) {
                                    <input #descInput class="meta-input card-desc" type="text"
                                        [ngModel]="gw.description" (ngModelChange)="gw.description = $event"
                                        (blur)="commitDescription(gw)"
                                        (keydown.enter)="commitDescription(gw)"
                                        (keydown.escape)="cancelEditDescription(gw)"
                                        placeholder="Name this gateway"
                                        [disabled]="savingUid === gw.uid" />
                                } @else {
                                    <!-- The same element as the Activation code value, so the two
                                         rows sit identically. An input in its place would drop its
                                         text by its own padding and border. -->
                                    <span class="value" [title]="gw.description || ''">{{ gw.description || '—' }}</span>
                                }
                            </div>
                            <div class="card-row card-row--stacked">
                                <span class="label">Activation code</span>
                                <span class="value">{{ gw.code || '—' }}</span>
                            </div>
                            <div class="card-row card-row--actions">
                                <div class="actions">
                                    @if (gw.active !== 0) {
                                        @if (gw.isConnected === 1) {
                                            <a class="pill connected" [href]="gw.connectUrl" target="_blank" rel="noopener">Show Gateway</a>
                                        } @else {
                                            <a class="pill disconnected" [href]="gw.connectUrl" target="_blank" rel="noopener">Not connected</a>
                                        }
                                        <form ngNoForm class="spy-form" [attr.action]="spyUrl" method="post" target="_blank" rel="noopener">
                                            <input type="hidden" name="db" [value]="gw.database || ''" />
                                            <button type="submit" class="pill spy">Open SPY</button>
                                        </form>
                                    } @else {
                                        <span class="pill inactive">Inactive</span>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                </div>
            }

            <div class="add-gateway">
                <h2 class="add-gateway__title">Add new gateway</h2>
                <form class="add-gateway__form" (ngSubmit)="activate()">
                    <label for="activationCode">Activation code</label>
                    <input id="activationCode" type="text" name="activationCode"
                        [(ngModel)]="newActivationCode" autocomplete="off" />
                    <button type="submit" [disabled]="activating || !newActivationCode.trim()">
                        Activate
                    </button>
                </form>
            </div>
        }
    </div>
</div>

<app-footer></app-footer>
